(() => {
  const normalize = s => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const houseURL = (root, house = '') => `${root}collections/all?view=fragrance-houses&sort_by=created-descending${house ? '#house=' + encodeURIComponent(house) : ''}`;
  let catalogue;
  async function getCatalogue(root) {
    if (!catalogue) catalogue = (async () => {
      const products = [];
      const seen = new Set();
      let page = 1;
      do {
        const url = new URL(`${root}collections/all`, location.origin);
        url.searchParams.set('view', 'bloom-catalog');
        url.searchParams.set('sort_by', 'created-descending');
        url.searchParams.set('page', page);
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Catalogue unavailable');
        const data = await response.json();
        if (!Array.isArray(data.products)) throw new Error('Invalid catalogue');
        for (const product of data.products) {
          if (!seen.has(product.id)) { seen.add(product.id); products.push(product); }
        }
        if (!data.next) break;
        page++;
        if (page > 200) throw new Error('Catalogue pagination incomplete');
      } while (true);
      return products.sort((a,b) => b.created - a.created || b.id - a.id);
    })().catch(error => { catalogue = null; throw error; });
    return catalogue;
  }
  function node(tag, text, className) {
    const el = document.createElement(tag);
    if (text != null) el.textContent = text;
    if (className) el.className = className;
    return el;
  }
  const housesOf = products => [...new Set(products.map(p => p.house).filter(Boolean))].sort((a,b) => normalize(a).localeCompare(normalize(b)));
  class BloomHouses extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const search = this.querySelector('input');
      const clear = this.querySelector('[data-clear]');
      const list = this.querySelector('[data-houses]');
      const status = this.querySelector('[data-status]');
      let initial = [...list.querySelectorAll('a')].map(a => ({name:a.textContent.trim(), href:a.href}));
      let letter = '';
      const render = () => {
        const term = normalize(search.value.trim());
        const entries = initial.filter(h => normalize(h.name).includes(term) && (!letter || normalize(h.name).startsWith(letter.toLowerCase())));
        list.replaceChildren(...entries.map(h => { const a=node('a',h.name); a.href=h.href; return a; }));
        clear.hidden = !search.value && !letter;
        status.textContent = entries.length ? `${entries.length} fragrance house${entries.length===1?'':'s'}` : 'No houses found. Try another name or clear your search.';
        this.querySelectorAll('[data-letter]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.letter === letter)));
      };
      search.addEventListener('input', render);
      clear.addEventListener('click', () => { search.value=''; letter=''; render(); search.focus(); });
      this.querySelectorAll('[data-letter]').forEach(b => b.addEventListener('click', () => { letter = letter === b.dataset.letter ? '' : b.dataset.letter; render(); }));
      render();
      getCatalogue(this.dataset.root).then(products => {
        initial = housesOf(products).map(name => ({name,href:houseURL(this.dataset.root,name)}));
        render();
      }).catch(() => { /* The complete saved directory stays usable if refresh fails. */ });
    }
  }
  class BloomDiscovery extends HTMLElement {
    async connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const root = this.dataset.root;
      const isNew = this.dataset.mode === 'new';
      const status = this.querySelector('[data-status]');
      const grid = this.querySelector('[data-products]');
      const controls = this.querySelector('[data-controls]');
      const search = this.querySelector('[data-search]');
      const house = this.querySelector('[data-house]');
      const sort = this.querySelector('[data-sort]');
      const clear = this.querySelector('[data-clear]');
      const more = this.querySelector('[data-more]');
      const readHash = key => new URLSearchParams(location.hash.slice(1)).get(key) || '';
      let gender = ['men','women','unisex'].includes(readHash('gender')) ? readHash('gender') : '';
      const saveHash = () => { const url=new URL(location.href);const hash=new URLSearchParams();if(house.value)hash.set('house',house.value);if(gender)hash.set('gender',gender);url.hash=hash.toString();history.replaceState(null,'',url); };
      const audienceOptions=this.querySelector('[data-audience-options]');
      const filterToggle = this.querySelector('[data-filter-toggle]');
      filterToggle?.addEventListener('click', () => {
        const expanded = filterToggle.getAttribute('aria-expanded') === 'true';
        filterToggle.setAttribute('aria-expanded', String(!expanded));
      });
      const selectedHouse = readHash('house');
      let shown = 36;
      try {
        let products = await getCatalogue(root);
        if (isNew) products = products.filter(p => p.isNew);
        for (const name of housesOf(products)) { const o = node('option', name); o.value=name; house.append(o); }
        if (selectedHouse && ![...house.options].some(o => o.value === selectedHouse)) { const o=node('option',selectedHouse); o.value=selectedHouse; house.append(o); }
        house.value=selectedHouse;
        const render = () => {
          const q = normalize(search.value.trim());
          const filtered = products.filter(p => (!house.value || p.house===house.value) && (!gender || p.audience===gender || (gender!=='unisex' && p.audience==='unisex')) && (!q || normalize(p.title+' '+p.house).includes(q)));
          const selectedSort = sort.value;
          filtered.sort((a,b) => selectedSort==='price-ascending' ? a.price-b.price : selectedSort==='price-descending' ? b.price-a.price : selectedSort==='title' ? normalize(a.title).localeCompare(normalize(b.title)) : b.created-a.created || b.id-a.id);
          grid.replaceChildren(...filtered.slice(0,shown).map(p => {
            const card=node('article',null,'bd-card');
            const imageLink=node('a',null,'bd-image'); imageLink.href=p.url; imageLink.tabIndex=-1; imageLink.setAttribute('aria-hidden','true');
            if (p.image) { const img=node('img'); img.src=p.image; img.alt=''; img.width=540; img.height=540; img.loading='lazy'; imageLink.append(img); }
            if (p.isNew) imageLink.append(node('span','New to Bloom','bd-badge'));
            if (!p.available) imageLink.append(node('span','Sold out','bd-stock'));
            const vendor=node('a',p.house,'bd-house'); vendor.href=houseURL(root,p.house);
            const title=node('h2'); const link=node('a',p.title); link.href=p.url; title.append(link);
            const price=node('p',(p.priceVaries ? 'From ' : '')+p.money,'bd-price');
            card.append(imageLink,vendor,title,price);
            if (p.audience) card.append(node('p',p.audience==='men'?'Men':p.audience==='women'?'Women':'Unisex','bd-audience-label'));
            if (p.format) card.append(node('p',p.format,'bd-format'));
            return card;
          }));
          status.textContent=filtered.length ? `${filtered.length} fragrance${filtered.length===1?'':'s'}${house.value?' from '+house.value:''}${gender ? ' · '+(gender==='unisex'?'Unisex':gender==='men'?'Men (including unisex)':'Women (including unisex)'):''}` : isNew && !products.length ? 'No recent additions right now. Explore all our fragrance houses below.' : 'No fragrances match. Remove a filter or try another search.';
          more.hidden=shown>=filtered.length;
          clear.hidden=!house.value && !search.value && !gender;
          audienceOptions.querySelectorAll('[data-audience]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.audience===gender)));
          clear.textContent='× Clear filters';
        };
        const resetRender=()=>{shown=36;render();};
        house.addEventListener('change',()=>{saveHash();resetRender();});
        audienceOptions.querySelectorAll('[data-audience]').forEach(button=>button.addEventListener('click',()=>{gender=gender===button.dataset.audience?'':button.dataset.audience;saveHash();resetRender();}));
        search.addEventListener('input',resetRender);
        sort.addEventListener('change',resetRender);
        clear.addEventListener('click',()=>{house.value='';search.value='';gender='';const url=new URL(location.href);url.hash='';history.replaceState(null,'',url);resetRender();search.focus();});
        more.addEventListener('click',()=>{const first=shown;shown+=36;render();grid.children[first]?.querySelector('h2 a')?.focus();});
        window.addEventListener('hashchange',()=>{house.value=readHash('house');gender=['men','women','unisex'].includes(readHash('gender'))?readHash('gender'):'';resetRender();});
        this.querySelector('[data-fallback]')?.remove();
        controls.hidden=false;
        audienceOptions.hidden=false;
        render();
      } catch (error) {
        status.textContent='The fragrance filters could not load. You can browse the fragrances below or reload this page to try again.';
        const fallback=this.querySelector('[data-fallback]');
        if (fallback) fallback.hidden=false;
      }
    }
  }
  if (!customElements.get('bloom-houses')) customElements.define('bloom-houses',BloomHouses);
  if (!customElements.get('bloom-discovery')) customElements.define('bloom-discovery',BloomDiscovery);
})();
